import { Component, app, ojs, payload } from "modular-openscriptjs";
import { either, formatThousand } from "../../helpers";
import MoreOptions from "../Blocks/MoreOptions";
import ProfilePic from "../ProfilePic";
import { dom } from "../../fotastart";
import CommentReplyForm from "../Forms/CommentReplyForm";

const h = app("h");
const gc = app("gc");
const broker = app("broker");
const $e = app("$e");

ojs(MoreOptions, ProfilePic, CommentReplyForm);

export default class CommentView extends Component {
	repliesShown = {};

	render(comment, ...args) {
		if (!comment?.id) return h.empty();

		let user = comment?.author;
		const isCurrentUser = user?.id == gc.user?.id;
		const isOwner = comment?.user_id == gc.user?.id;

		return h.div(
			{
				class: "d-flex p-0 gap-3 position-relative",
				data_comment_id: comment?.id,
				$data_comment_id: comment?.id,
			},

			// Left Column: Avatar
			h.div(
				{ class: "flex-shrink-0" },
				h.ProfilePic(user, {
					class: "rounded-circle d-block border border-light-subtle",
					style: "width: 40px; height: 40px; object-fit: cover;",
				})
			),

			// Right Column: Content
			h.div(
				{ class: "d-flex flex-column flex-fill" },

				// Header: User Info & Time
				h.div(
					{
						class: "d-flex justify-content-between align-items-start",
					},
					h.div(
						{ class: "d-flex flex-column" },
						h.div(
							{
								class: "d-flex align-items-center gap-1",
							},
							h.span(
								{
									class: "fw-bold text-dark fs-sm cursor-pointer",
									onclick: h.func(
										"route.to",
										"user.view-profile",
										{
											user: isCurrentUser
												? "current"
												: user?.id,
										}
									),
								},
								user?.display_name
							),
							either(user?.is_verified, () =>
								h.i({
									class: "fas fa-circle-check text-info fs-xxsm",
								})
							),
							either(user?.is_business, () =>
								h.i({ class: "fas fa-shop text-info fs-xxsm" })
							)
						),
						h.span(
							{ class: "text-secondary fs-xsm" },
							"@",
							user?.username,
							h.span({ class: "mx-1" }, "•"),
							comment?.shared_at
						)
					),

					// More Options
					elementIf(isOwner, () =>
						h.MoreOptions([
							null,
							h.ul(
								{ class: "list-unstyled m-0" },
								h.li(
									h.div(
										{
											role: "button",
											class: "dropdown-item dropdown-item-danger btn-icon-text gap-2 px-3 py-2 text-danger",
											title: "Delete comment",
											onclick: this.method(
												"delete",
												comment
											),
										},
										h.i({
											class: "fa-solid fa-trash-can w-20px",
										}),
										h.span("Delete")
									)
								)
							),
						])
					)
				),

				// Comment Body
				h.div(
					{ class: "mt-1 mb-2" },
					either(comment?.recipient, () =>
						h.div(
							{ class: "mb-1 fs-sm" },
							h.span({ class: "text-secondary" }, "Replying to "),
							h.span(
								{ class: "text-primary fw-medium" },
								"@",
								comment?.recipient
							)
						)
					),
					h.fromString(comment?.content, "div", {
						class: "text-dark text-break fs-sm",
					})
				),

				// Footer: Actions
				h.div(
					{ class: "d-flex align-items-center gap-4 text-secondary" },

					// Like Button
					h.span(
						{
							class: "d-flex gap-1 align-items-center cursor-pointer fs-xsm hover-text-danger transition-colors",
						},
						either(
							comment?.is_liked,
							() => [
								h.i({
									class: "fas fa-heart text-danger animated-icon animated-add",
								}),
								{ onclick: this.method("unlike", comment) },
							],
							() => [
								h.i({
									class: "far fa-heart animated-icon animated-remove",
								}),
								{ onclick: this.method("like", comment) },
							]
						),
						formatThousand(comment?.like_count ?? 0)
					),

					// Reply Button
					h.span(
						{
							class: "d-flex gap-1 align-items-center cursor-pointer fs-xsm hover-text-primary transition-colors",
							onclick: this.method("comment", comment),
						},
						either(
							comment?.has_commented,
							() =>
								h.i({
									class: "fas fa-comment text-primary animated-icon animated-add",
								}),
							() =>
								h.i({
									class: "far fa-comment",
								})
						),
						formatThousand(comment?.comment_count ?? 0)
					)
				),

				// View Replies Toggle
				either(comment.comment_count > 0, () =>
					h.div(
						{ class: "mt-2" },
						h.span(
							{
								class: "text-primary fs-xsm fw-medium cursor-pointer",
								onclick: this.method("showReplies", [
									comment,
									"${this}",
								]),
							},
							either(
								`c-${comment.id}` in this.repliesShown,
								[{ replies_shown: true }, "Hide replies"],
								[{ replies_shown: false }, "View replies"]
							)
						)
					)
				)
			),
			...args
		);
	}

	delete(comment) {
		broker.send($e.comment.needs.deletion, payload({ comment }));
	}

	like(comment) {
		broker.send($e.comment.needs.like, payload({ comment }));
	}

	unlike(comment) {
		broker.send($e.comment.needs.unlike, payload({ comment }));
	}

	comment(comment) {
		broker.send($e.comment.needs.reply, payload({ comment }));
	}

	showReplies(comment, elem) {
		let repliesShown = elem.getAttribute("replies-shown");

		if (repliesShown == "false") {
			dom.put("Hide replies", elem);
			elem.setAttribute("replies-shown", "true");
			this.repliesShown[`c-${comment.id}`] = true;
			broker.send($e.comment.needs.replies, payload({ comment }));
		} else {
			dom.put("View replies", elem);
			elem.setAttribute("replies-shown", "false");
			delete this.repliesShown[`c-${comment.id}`];
			broker.send($e.comment.needs.to.hideReplies, payload({ comment }));
		}
	}

	cleanUp() {
		super.cleanUp();
		this.repliesShown = {};
	}

	$$ojs = {
		beforeRouteChange: () => this.cleanUp(),
	};
}

function elementIf(condition, renderFn) {
	return condition ? renderFn() : null;
}
